import { Link } from "react-router-dom";
import { COMPANY } from "../lib/content";
import { Reveal, MaskReveal } from "../components/Reveal";
import ClientFilmStrip from "../components/ClientFilmStrip";

export default function About() {
  return (
    <div className="pt-32 md:pt-40 pb-32" data-testid="about-page">
      <section className="container-edge grid grid-cols-12 gap-y-10 gap-x-6">
        <div className="col-span-12 md:col-span-7">
          <div className="overline text-ink/60">Chapter 01 — The House · Established 2016</div>
          <MaskReveal delay={0.1}>
            <h1 className="font-display uppercase text-7xl md:text-9xl leading-[0.85] mt-6">
              Built like a magazine. <span className="serif-italic font-serif normal-case">Run like an atelier.</span>
            </h1>
          </MaskReveal>
        </div>
        <div className="col-span-12 md:col-span-5 self-end">
          <Reveal delay={0.3}>
            <p className="text-lg md:text-xl text-ink/80 font-light leading-relaxed">
              Founded by Meghan Amaral, M Media Corp is the in-house creative, PR and production and media house for the women and houses who insist on being told beautifully. We don&apos;t outsource taste — and we don&apos;t take on names. We take on partnerships.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="container-edge mt-24 md:mt-32 grid grid-cols-12 gap-y-10 gap-x-6 items-end">
        <div className="col-span-12 md:col-span-5">
          <div className="overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/d3qr5lx3_IMG_5463.jpeg"
              alt="Meghan Amaral, Founder & CEO of M Media Corp"
              loading="lazy"
              decoding="async"
              className="w-full aspect-[4/5] object-cover img-bw"
              data-testid="ceo-portrait"
            />
          </div>
          <div className="overline text-ink/50 mt-4">{COMPANY.ceo} — {COMPANY.ceoTitle}</div>
        </div>
        <div className="col-span-12 md:col-span-6 md:col-start-7">
          <Reveal>
            <p className="serif-italic font-serif text-3xl md:text-4xl leading-[1.1] text-ink">
              &ldquo;When one woman wins, <span className="not-italic font-display uppercase">we all win.</span>&rdquo;
            </p>
            <p className="overline text-ink/50 mt-6">— Meghan Amaral, CEO</p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER'S LETTER */}
      <section className="container-edge mt-24 md:mt-32" data-testid="founders-letter">
        <div className="grid grid-cols-12 gap-y-10 gap-x-8">
          <div className="col-span-12 md:col-span-4">
            <div className="overline text-ink/55">Chapter 01.5 — A Letter from the Founder</div>
            <MaskReveal delay={0.05}>
              <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.85] mt-5">
                A camera, <br/>
                <span className="serif-italic font-serif normal-case">a dream</span>, <br/>
                and the women <br/>
                <span className="serif-italic font-serif normal-case">who said yes</span>.
              </h2>
            </MaskReveal>
          </div>

          <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-7 text-ink/85 leading-[1.7] text-lg md:text-xl font-light">
            <Reveal>
              <p>
                I started M Media alone, at twenty-one — with nothing but a camera, a dream, and a refusal to be small about either. From the first day I launched, I was given the privilege of working with powerhouse women who put me on the map. I owe so much of what I have built to their early belief in me, and I will spend the rest of my career honouring it. M Media is the room I am building back for them — and for the women still coming.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p>
                An M Media client is one I personally admire. Each one is an inspiration — a woman whose vision I want the world to see at the scale her work deserves. We don&apos;t take on names; we take on partnerships. What we invest is not labour or strategy alone — it is sincere care, hard-earned trust, and a memory for every detail that ever mattered to them. That is why our clients stay. Their wins become wins we celebrate together; their second act becomes the third we plan over a long dinner.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                M Media is for women who empower other women. In a media world that inflates egos, we deliberately accept new powerful women who have something to say and something to change — <span className="serif-italic font-serif">artists, speakers, politicians, healers, authors, business owners, designers, celebrities, philanthropists, creatives, founders</span>, and every wonderful thing in between. To be an M Media client is to be a woman of impact. A woman of service. A woman whose story is being told because the world is better when she is at full volume.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p>
                When you trust M Media with your story, you are not booking a campaign — you are building a legacy. One that will keep inspiring long after the cover ships. Because here, the work is never really about us.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="serif-italic font-serif text-2xl md:text-3xl text-ink leading-snug pt-2">
                When one woman wins, we all win.
              </p>
              <p className="overline text-ink/50 mt-4">— Meghan Amaral · Newport Beach · Established 2016</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="container-edge mt-32 md:mt-40 grid grid-cols-12 gap-y-12 gap-x-8">
        {[
          { n: "01", t: "Editorial-first", d: "Every campaign is treated like a cover story. Layouts, sequencing, narrative — designed to be remembered." },
          { n: "02", t: "Partnership-led", d: "We are not vendors. Our retainers come with strategists, creatives and producers who know your name." },
          { n: "03", t: "Women at the helm", d: "A house built by — and largely for — powerful women. Our clients see themselves on every wall here." },
          { n: "04", t: "Future-fluent", d: "From AI personalization to holographic skylines (2028), we operate where the next decade is being designed." },
        ].map((p, i) => (
          <Reveal key={p.n} delay={i * 0.05} className="col-span-12 md:col-span-6 lg:col-span-3">
            <div className="overline text-ink/50">{p.n}</div>
            <h3 className="font-display uppercase text-4xl mt-3">{p.t}</h3>
            <p className="text-ink/75 mt-3 leading-relaxed">{p.d}</p>
          </Reveal>
        ))}
      </section>

      {/* IN THE ROOM — duo editorial spread */}
      <section className="container-edge mt-32 md:mt-40" data-testid="about-in-the-room">
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 items-end mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="overline text-ink/55">Chapter 02 — In the Room</div>
            <MaskReveal delay={0.05}>
              <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.85] mt-4">
                On the floor.<br/><span className="serif-italic font-serif normal-case">In the rooms that matter.</span>
              </h2>
            </MaskReveal>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <Reveal delay={0.2}>
              <p className="text-ink/75 leading-relaxed">
                We don&apos;t post about the work from a desk. Meghan and the senior partners are with the founders — at the galas, in New York, on the campaign floor — wherever the cover gets shot.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-8">
          <Reveal className="col-span-12 md:col-span-6">
            <figure>
              <div className="overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/1l9gzp31_IMG_5231.jpeg"
                  alt="Meghan Amaral with Susan Grau in New York City"
                  loading="lazy"
                  decoding="async"
                  className="w-full aspect-[4/5] object-cover img-bw hover:grayscale-0 transition-all duration-1000"
                  data-testid="about-photo-susan"
                />
              </div>
              <figcaption className="flex items-baseline justify-between mt-4">
                <span className="font-serif italic text-ink/85 text-lg">With Susan Grau</span>
                <span className="overline text-ink/45">New York City, NY · 2026</span>
              </figcaption>
            </figure>
          </Reveal>
          <Reveal delay={0.1} className="col-span-12 md:col-span-6">
            <figure>
              <div className="overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_brand-proposal-site/artifacts/5hlztqg5_Tezza-0894.jpeg"
                  alt="Meghan Amaral with Julia Post — Julia Post Jewelry campaign"
                  loading="lazy"
                  decoding="async"
                  className="w-full aspect-[4/5] object-cover img-bw hover:grayscale-0 transition-all duration-1000"
                  data-testid="about-photo-julia"
                />
              </div>
              <figcaption className="flex items-baseline justify-between mt-4">
                <span className="font-serif italic text-ink/85 text-lg">With Julia Post-Guenther</span>
                <span className="overline text-ink/45">Santa Monica, CA · 2026</span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* Film-strip of additional clients — "and everywhere in between" */}
        <Reveal>
          <ClientFilmStrip testid="about-client-filmstrip" />
        </Reveal>
      </section>

      <section className="bg-ink text-cream mt-32 py-24 md:py-32">
        <div className="container-edge grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-6">
            <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.85]">By the<br/>numbers.</h2>
          </div>
          <div className="col-span-12 md:col-span-6 grid grid-cols-2 gap-8">
            {[
              { n: "120+", l: "Brands shaped" },
              { n: "9", l: "Disciplines in-house" },
              { n: "2.4B", l: "Combined audience reach" },
              { n: "26", l: "Award placements" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-7xl md:text-8xl leading-none">{s.n}</div>
                <div className="overline text-cream/60 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-edge py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-display uppercase text-5xl sm:text-6xl md:text-8xl leading-[0.85]">
            The next <span className="serif-italic font-serif normal-case">cover</span> is yours.
          </h2>
          <Link to="/apply" className="btn-ink mt-10 inline-flex" data-testid="about-cta">Request an application</Link>
        </Reveal>
      </section>
    </div>
  );
}
